import { useState, useEffect, useRef } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "react-toastify";
import Footer from "../components/layout/Footer";
import { getTrips, getTrip, getMyPublishedPosts } from "../services/api";
import { hydrateStopsFromTrip } from "../utils/stopsMigration";

import {
  ItineraryCard,
  EmptyState,
  PlanNewCard,
  PlaneIcon,
  SearchIcon,
  useDebounce,
} from "../components/dashboard";
import PublishedPostCard from "../components/dashboard/PublishedPostCard";
import ResubmitFlow from "../components/dashboard/ResubmitFlow";
import OngoingTripCard from "../components/dashboard/OngoingTripCard";
import { useMyProfile } from "../hooks/useProfile";
import { trackEvent } from "../utils/analytics";
import * as Sentry from "@sentry/react";
import { SystemError } from "../errors/sentryErrors";

// 1_200 -> "1.2K". Keeps the stat row a fixed width once a follower count grows.
const formatCount = (n) => {
  const value = Number(n) || 0;
  if (value >= 1000000) return `${(value / 1000000).toFixed(value % 1000000 === 0 ? 0 : 1)}M`;
  if (value >= 1000) return `${(value / 1000).toFixed(value % 1000 === 0 ? 0 : 1)}K`;
  return String(value);
};

const MegaphoneIcon = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
    <path d="M3 11v2a1 1 0 001 1h2l4 4V6L6 10H4a1 1 0 00-1 1z" />
    <path d="M15 8.5a4 4 0 010 7" />
    <path d="M18 6a7 7 0 010 12" />
  </svg>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const { data: myProfile } = useMyProfile();
  const profileStats = myProfile?.stats;
  const [itineraries, setItineraries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [itineraryTab, setItineraryTab] = useState("upcoming"); // 'upcoming' | 'ongoing' | 'completed'
  // Top-level switch between the two things this page holds. They used to be
  // stacked sections; as tabs the community posts are reachable without
  // scrolling past every trip.
  const [mainTab, setMainTab] = useState("itineraries"); // 'itineraries' | 'community'

  // Whole-account counts from the API. Derived counts would only ever describe
  // the page currently loaded (12 of 76).
  const [tripCounts, setTripCounts] = useState(null);
  const [publishedPosts, setPublishedPosts] = useState([]);
  const [loadingPosts, setLoadingPosts] = useState(false);
  const [resubmitPostState, setResubmitPostState] = useState(null);
  const [searchParams, setSearchParams] = useSearchParams();

  const [pagination, setPagination] = useState({
    page: 1,
    limit: 12,
    totalCount: 0,
    totalPages: 0,
    hasMore: false
  });

  const debouncedSearch = useDebounce(searchQuery, 300);

  useEffect(() => {
    document.title = "Dashboard | Romaya AI Trip Planner";
  }, []);

  // Both still load on mount even though only one tab is visible: each tab's
  // pill shows its own count, so the inactive one's data is needed immediately,
  // and switching tabs is then instant. Posts do not depend on the search box,
  // hence the separate effect.
  useEffect(() => {
    fetchItineraries(1, true);
  }, [debouncedSearch, itineraryTab]);

  useEffect(() => {
    fetchPublishedPosts();
  }, []);

  // A "your post was rejected" notification links here as
  // /dashboard?resubmit=<slug> (see NotificationRow.jsx) — /community/:slug/edit
  // was never a registered route. Once the posts are in, open that specific
  // post's Fix and Resubmit flow automatically, then drop the query param so
  // reloading or closing/reopening the modal doesn't re-trigger it.
  useEffect(() => {
    const resubmitSlug = searchParams.get("resubmit");
    if (!resubmitSlug || publishedPosts.length === 0) return;

    const match = publishedPosts.find((p) => p.slug === resubmitSlug);
    if (match) {
      setResubmitPostState(match);
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        next.delete("resubmit");
        return next;
      }, { replace: true });
    }
  }, [publishedPosts, searchParams, setSearchParams]);

  // getMyPublishedPosts is cursor-paginated, not page-numbered — this used to
  // take a `page` argument that the underlying API call silently ignored, so
  // it always re-fetched the same first page regardless of what was passed.
  // Neither call site below actually needs anything but the first page right
  // now (both are full resets: initial mount, and after a resubmit), so this
  // stays a plain reset-fetch rather than growing unused "load more" plumbing.
  const fetchPublishedPosts = async () => {
    try {
      setLoadingPosts(true);
      setError(null);
      const response = await getMyPublishedPosts(null, 12);
      setPublishedPosts(response.posts || response || []);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "system");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("dashboard", { section: "published_posts" });
        Sentry.captureException(new SystemError(err instanceof Error ? err.message : String(err)));
      });
      console.error("Error fetching published posts:", err);
      setError(err.message);
    } finally {
      setLoadingPosts(false);
    }
  };

  const fetchItineraries = async (page = 1, isNewSearch = false) => {
    try {
      if (isNewSearch) {
        setLoading(true);
        setSearching(!!debouncedSearch);
      } else {
        setLoadingMore(true);
      }
      setError(null);

      const response = await getTrips({
        page,
        limit: 12,
        search: debouncedSearch,
        // 'completed' is the client's word for it; the API calls it 'past'.
        bucket: itineraryTab === 'completed' ? 'past' : itineraryTab,
      });

      const trips = response.trips || response || [];
      const paginationData = response.pagination || {
        page: 1,
        limit: 12,
        totalCount: trips.length,
        totalPages: 1,
        hasMore: false
      };

      if (isNewSearch) {
        setItineraries(trips);
      } else {
        setItineraries(prev => [...prev, ...trips]);
      }

      setPagination(paginationData);
      if (response.counts) setTripCounts(response.counts);
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "system");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("dashboard", { section: "itineraries" });
        Sentry.captureException(new SystemError(err instanceof Error ? err.message : String(err)));
      });
      console.error("Error fetching itineraries:", err);
      setError(err.message);
    } finally {
      setLoading(false);
      setLoadingMore(false);
      setSearching(false);
    }
  };

  const handleLoadMore = () => {
    if (pagination?.hasMore && !loadingMore) {
      fetchItineraries((pagination?.page || 1) + 1, false);
    }
  };

  // Infinite scroll, replacing the "Load More" button. Pages are still fetched
  // 12 at a time rather than all at once: the list endpoint returns each trip's
  // full record, `itinerary` JSON blob included, so pulling all 77 in one
  // request would be a multi-megabyte response for a page that shows cards.
  //
  // The sentinel sits below the grid and loads the next page as it comes into
  // view. When the community tab is showing, the whole section is `display:
  // none`, so the sentinel never intersects and nothing loads behind it.
  const loadMoreSentinelRef = useRef(null);

  useEffect(() => {
    const el = loadMoreSentinelRef.current;
    if (!el || !pagination?.hasMore || loading || loadingMore) return;
    if (typeof IntersectionObserver === "undefined") return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) handleLoadMore();
      },
      // Start fetching slightly before the sentinel is actually on screen, so
      // the next cards are usually there by the time the user reaches them.
      { rootMargin: "300px" }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, [pagination?.hasMore, pagination?.page, loading, loadingMore]);

  const handleViewDetails = async (trip) => {
    try {
      const fullTrip = await getTrip(trip.id);
      navigate("/itinerary", {
        state: {
          itinerary: fullTrip.itinerary,
          tripData: {
            startingLocation: fullTrip.startingLocation,
            startingLocationData: fullTrip.startingLocationData,
            destination: fullTrip.destination,
            destinationData: fullTrip.destinationData,
            destinations: fullTrip.destinations || [],
            stops: hydrateStopsFromTrip(fullTrip),
            travelStyle: fullTrip.travelStyle || "",
            startDate: fullTrip.startDate?.split("T")[0] || fullTrip.startDate,
            endDate: fullTrip.endDate?.split("T")[0] || fullTrip.endDate,
            themes: fullTrip.themes || [],
            weather: fullTrip.weather || [],
            accommodation: fullTrip.accommodation || [],
            food: fullTrip.food || [],
            travelToDest: fullTrip.travelToDest,
            travelBetween: fullTrip.travelBetween || [],
            adults: fullTrip.adults,
            children: fullTrip.children,
            infants: fullTrip.infants || 0,
            rooms: fullTrip.rooms,
            budget: fullTrip.budget,
            currency: fullTrip.currency,
            preferences: fullTrip.preferences,
            customNightsPerDestination: fullTrip.customNightsPerDestination,
          },
          tripId: fullTrip.id,
          fromDashboard: true
        }
      });
    } catch (err) {
      Sentry.withScope((scope) => {
        scope.setTag("feature", "system");
        scope.setTag("app", "web");
        scope.setTag("error_source", err instanceof TypeError ? "network" : "server");
        scope.setContext("dashboard", { section: "trip_details", trip_id: trip.id });
        Sentry.captureException(new SystemError(err instanceof Error ? err.message : String(err)));
      });
      console.error("Error fetching trip details:", err);
      toast.error(err.message || "Failed to load trip details. Please try again.");
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 flex flex-col">
      <main className="pt-24 pb-16 flex-1">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
            <div className="min-w-0">
              {/* Credits now live in the header (visible on every app route),
                  not just here — see Navbar's CreditsBadge. */}
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-1 tracking-[-0.02em]">
                My Travel Dashboard
              </h1>
              <p className="text-gray-500">
                Manage your upcoming adventures, completed journeys, and travel stories.
              </p>
            </div>
            <Link
              to="/plan"
              className="inline-flex items-center justify-center px-5 py-2.5 bg-teal-600 text-white font-semibold rounded-full hover:bg-teal-700 transition-colors shadow-sm whitespace-nowrap cursor-pointer"
              onClick={() => trackEvent("create_new_trip_clicked")}
            >
              <span className="mr-1">+</span>
              Create New Trip
            </Link>
          </div>

          {/* Social proof and wallet, right-aligned under the header as in the
              design. Every figure comes from the profile endpoint — no
              placeholder counts. The whole row is skipped until that profile
              loads; rendering it empty left a tall blank gap above the trips. */}
          {profileStats && (
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 mb-8">
              {/* Counts strip. Every figure comes from /api/users/me/profile —
                  no placeholders. */}
              <div className="bg-white rounded-xl border border-gray-200/70 shadow-sm inline-flex divide-x divide-gray-100 self-stretch sm:self-start">
                {[
                  { label: "Followers", value: profileStats.followerCount },
                  { label: "Following", value: profileStats.followingCount },
                  { label: "Community itineraries", value: profileStats.publishedTrips },
                ].map((stat) => (
                  <div
                    key={stat.label}
                    className="min-w-0 px-3 sm:px-5 py-4 flex items-baseline gap-1.5 sm:whitespace-nowrap flex-1 sm:flex-none justify-center"
                  >
                    <span className="text-base font-bold text-teal-700 tabular-nums shrink-0">
                      {formatCount(stat.value ?? 0)}
                    </span>
                    <span className="text-[13px] text-gray-500">{stat.label}</span>
                  </div>
                ))}
              </div>

              <div className="flex items-center gap-3 w-full sm:w-auto sm:shrink-0">
                <div className="relative flex-1 min-w-0 sm:flex-none">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    {searching ? (
                      <svg className="w-5 h-5 text-teal-500 animate-spin" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    ) : (
                      <SearchIcon />
                    )}
                  </div>
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search itineraries..."
                    className="w-full sm:w-64 pl-10 pr-8 py-2.5 bg-white border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent shadow-sm"
                  />
                  {searchQuery && (
                    <button
                      onClick={() => setSearchQuery("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Top-level tabs. The counts describe the whole account, not the
              loaded page — `tripCounts.all` rather than `pagination.totalCount`,
              which only ever counts the bucket currently selected below. */}
          <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
            <div
              className="flex flex-nowrap items-center gap-2 overflow-x-auto scrollbar-hidden"
              role="tablist"
              aria-label="Dashboard sections"
            >
              {[
                {
                  key: "itineraries",
                  label: "My Itineraries",
                  shortLabel: "Itineraries",
                  count: tripCounts?.all,
                  icon: <PlaneIcon />,
                },
                {
                  key: "community",
                  label: "My Community Posts",
                  shortLabel: "Community",
                  count: publishedPosts.length,
                  icon: <MegaphoneIcon />,
                },
              ].map((tab) => {
                const isActive = mainTab === tab.key;
                return (
                  <button
                    key={tab.key}
                    role="tab"
                    aria-selected={isActive}
                    onClick={() => setMainTab(tab.key)}
                    className={`shrink-0 px-3.5 py-2 sm:px-5 sm:py-2.5 rounded-full text-xs sm:text-sm font-semibold transition-all inline-flex items-center gap-1.5 sm:gap-2 cursor-pointer ${
                      isActive
                        ? "bg-teal-700 text-white shadow-sm"
                        : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    <span className={isActive ? "text-white/90" : "text-gray-400"}>{tab.icon}</span>
                    {/* Full label wraps every button past the phone-width fit
                        check; the short form drops "My "/"Posts" so both tabs
                        share one row without the overflow-x scroll clipping
                        the second one mid-word. */}
                    <span className="sm:hidden">{tab.shortLabel}</span>
                    <span className="hidden sm:inline">{tab.label}</span>
                    {tab.count > 0 && (
                      <span className={`tabular-nums ${isActive ? "text-white/80" : "text-gray-400"}`}>
                        ({tab.count})
                      </span>
                    )}
                  </button>
                );
              })}
            </div>

            <Link
              to={mainTab === "itineraries" ? "/community/search" : "/community"}
              className="text-sm font-semibold text-teal-700 hover:text-teal-800 whitespace-nowrap"
            >
              {mainTab === "itineraries" ? "Explore community →" : "See all posts →"}
            </Link>
          </div>

          <section className={`mb-14 ${mainTab === "itineraries" ? "" : "hidden"}`}>
            <>
                {loading && (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-teal-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}

            {error && !loading && (
              <div className="text-center py-8">
                <p className="text-red-500 mb-4">{error}</p>
                <button
                  onClick={fetchItineraries}
                  className="text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                >
                  Try Again
                </button>
              </div>
            )}

            {/* "No itineraries yet" belongs to an account with no trips at all.
                Keyed off the loaded page it fired whenever the selected tab was
                empty — hiding the tabs, so 76 past trips looked like none. */}
            {!loading && !error && !searchQuery && tripCounts?.all === 0 && (
              <EmptyState />
            )}

            {!loading && !error && (searchQuery || tripCounts?.all !== 0) && (
              <>
                {/* Horizontal tabs, one group at a time. */}
                {(() => {
                  // The API returns only the active bucket now, so the cards are
                  // `itineraries` as-is; the counts come from the server and
                  // describe the whole account, not the loaded page.
                  // Ongoing only shows up as its own tab when there's actually a
                  // trip happening right now — otherwise it's just a permanently
                  // empty tab nobody needs to see.
                  const GROUPS = [
                    ...(tripCounts?.ongoing > 0
                      ? [{ key: 'ongoing', label: 'Ongoing', count: tripCounts?.ongoing }]
                      : []),
                    { key: 'upcoming', label: 'Upcoming', count: tripCounts?.upcoming },
                    { key: 'completed', label: 'Past', count: tripCounts?.past },
                  ];
                  const active = GROUPS.find((g) => g.key === itineraryTab) || GROUPS[0];
                  const activeTrips = itineraries;
                  const showPlanCard = active.key === 'upcoming' && !searchQuery;

                  return (
                    <>
                      <div className="flex flex-wrap items-center gap-2 mb-6">
                        {GROUPS.map((group) => {
                          const isActive = group.key === active.key;
                          return (
                            <button
                              key={group.key}
                              onClick={() => setItineraryTab(group.key)}
                              className={`px-4 py-2 rounded-full text-sm font-semibold transition-all flex items-center gap-2 cursor-pointer ${
                                isActive
                                  ? 'bg-teal-600 text-white shadow-sm'
                                  : 'bg-white text-gray-600 border border-gray-200 hover:bg-gray-50'
                              }`}
                            >
                              {group.label}
                              {/* A trip happening right now is worth flagging
                                  before the tab is opened. */}
                              {group.key === 'ongoing' && group.count > 0 && !isActive && (
                                <span className="w-2 h-2 rounded-full bg-teal-500 animate-pulse-soft" aria-hidden="true" />
                              )}
                              {group.count > 0 && (
                                <span className={`text-xs font-semibold tabular-nums ${isActive ? 'text-white/80' : 'text-gray-400'}`}>
                                  {group.count}
                                </span>
                              )}
                            </button>
                          );
                        })}
                      </div>

                      <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                        {activeTrips.map((trip) =>
                          active.key === 'ongoing' ? (
                            <OngoingTripCard
                              key={trip.id}
                              trip={trip}
                              onViewDetails={handleViewDetails}
                            />
                          ) : (
                            <ItineraryCard
                              key={trip.id}
                              trip={trip}
                              onViewDetails={handleViewDetails}
                              onTripUpdated={() => fetchItineraries(1, true)}
                            />
                          ),
                        )}
                        {showPlanCard && <PlanNewCard />}
                      </div>

                      {/* Upcoming always has the "plan a new trip" card, so an
                          empty message there would sit beside a live action. */}
                      {activeTrips.length === 0 && !showPlanCard && (
                        <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                          <p className="text-gray-500">
                            {searchQuery
                              ? `No ${active.label.toLowerCase()} trips match "${searchQuery}".`
                              : active.key === 'ongoing'
                                ? 'No trips in progress right now.'
                                : 'No past trips yet.'}
                          </p>
                          {searchQuery && (
                            <button
                              onClick={() => setSearchQuery("")}
                              className="mt-3 text-teal-600 hover:text-teal-700 font-medium cursor-pointer"
                            >
                              Clear search
                            </button>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}

                {/* Scroll sentinel — the next page loads when this comes into
                    view. Zero-height and inert; the spinner below is the only
                    thing the user sees while a page is in flight. */}
                {pagination?.hasMore && (
                  <div ref={loadMoreSentinelRef} aria-hidden="true" className="h-px" />
                )}

                {loadingMore && (
                  <div className="flex justify-center mt-8" role="status" aria-live="polite">
                    <svg className="w-6 h-6 animate-spin text-teal-600" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="sr-only">Loading more itineraries</span>
                  </div>
                )}

                {/* Without a button there is no longer anything marking the end
                    of a long list, so say so once everything is loaded. */}
                {!pagination?.hasMore && !loadingMore && itineraries.length >= 12 && (
                  <p className="text-center text-sm text-gray-400 mt-8">
                    All {pagination?.totalCount || itineraries.length} itineraries loaded.
                  </p>
                )}
              </>
            )}
            </>
          </section>

          {/* Community posts. Now a tab beside the itineraries rather than a
              section stacked below them, so it is reachable without scrolling
              past every trip. Hidden rather than unmounted so switching back and
              forth keeps its loaded data and does not refetch. The heading and
              "See all posts" link live in the tab bar above. */}
          <section className={`mb-12 ${mainTab === "community" ? "" : "hidden"}`}>
            <p className="text-sm text-gray-500 mb-5">
              Share your finalised itineraries and inspire others.
            </p>

            {loadingPosts ? (
              <div className="flex items-center justify-center py-16">
                <div className="w-8 h-8 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
              </div>
            ) : publishedPosts.length === 0 ? (
              <div className="text-center py-12 bg-white rounded-2xl border border-gray-100">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">No published posts</h3>
                <p className="text-gray-500 mb-4">
                  Finish a trip, write its review, and it appears here for the community.
                </p>
              </div>
            ) : (
              <div className="grid gap-6 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3">
                {publishedPosts.map((post) => (
                  <PublishedPostCard
                    key={post.id}
                    post={post}
                    onResubmitClick={() => setResubmitPostState(post)}
                  />
                ))}
              </div>
            )}
          </section>

          <ResubmitFlow 
            isOpen={!!resubmitPostState} 
            post={resubmitPostState} 
            onClose={() => setResubmitPostState(null)} 
            onResubmitSuccess={() => fetchPublishedPosts()} 
          />

        </div>
      </main>

      <Footer />
    </div>
  );
}
